// Champions UI relabels. Pokémon Champions renames several SV-era concepts:
//   - "Nature" → "Stat Alignment"
//   - "EVs" → "Stat Points" (one day; not yet swapped — Pikalytics, the
//     calc community, and most user docs still say EVs, so we leave that
//     for now and revisit when the rename has stuck in the wider corpus)
//
// The DATA MODEL stays SV-named — server schemas, @smogon/calc field names,
// and POSTed payloads still use `nature`, `evs`, etc. This module is the
// single point where user-facing strings translate. Touch the LABELS here
// when a relabel is needed; never rename the underlying fields.

// Display label for the player-visible "nature" concept. Used by editor /
// rail / damage card so the term updates everywhere from one edit.
export const NATURE_LABEL = {
  caps: 'STAT ALIGNMENT',
  title: 'Stat Alignment',
  lower: 'stat alignment',
  // Compact form for tight grids (EV row glyphs, narrow chips). Plural-form
  // unchanged; we'd say "Stat Alignment" in most labels.
  short: 'alignment',
} as const;

// Pokémon Champions cut Hardy / Docile / Bashful / Quirky from the picker;
// only Serious remains as a neutral Stat Alignment. Mechanically all 25
// still resolve to 0.9/1.0/1.1 multipliers (we don't validate against this
// set server-side), but the picker UI should only offer what the game does.
//
// Source: vgcbuddy/docs Pokémon Champions Stat Point doc (2026-05).
const DROPPED_NEUTRAL_NATURES = new Set(['Hardy', 'Docile', 'Bashful', 'Quirky']);

export function isChampionsLegalNature(nature: string): boolean {
  return !DROPPED_NEUTRAL_NATURES.has(nature);
}

// Filter a list of Gen-9 nature names to those Champions still offers.
// Stable order — caller's input order is preserved.
export function filterChampionsNatures<T extends { id?: string; name?: string } | string>(
  natures: readonly T[],
): T[] {
  return natures.filter((n) => {
    const name = typeof n === 'string' ? n : (n.id ?? n.name ?? '');
    return isChampionsLegalNature(name);
  });
}
